import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { fetchConsolidationDetails } from 'actions/gigs'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import { Body1, ButtonText } from 'components/layout/text'
import Panel, { PanelTitle, PanelSpacer } from 'components/layout/Panel'
import { StaticLoader } from 'components/misc/Loader'
import { useTheme } from 'styled-components'
import Icon from 'components/misc/Icon'
import { formatCurrency, capitalize, roundUpToOneDecimal } from 'util/format'
import flatMap from 'lodash/flatMap'
import partition from 'lodash/partition'
import { ExternalLink, AdaptiveExternalLink } from 'components/layout/links'
import { Box, Button, Container, Form, parsers, Icon as RoadieUIIcon, IconStack, Spacer, Text } from 'roadie-ui'
import { Modal } from 'roadie-ui'
import Gallery from 'rui/widgets/Gallery'
import Photo from 'components/form/Photo'
import { api } from 'roadie-ui'
import { MapDisplay } from 'components/pages/MapDisplay'
import { getLatestAccountSetting } from 'util/profile'
import { getGoogleTranslateTargetLanguage, translateText } from 'util/translate'
import { useBlockData } from 'hooks/useBlockData'
import {
  getEnglishTextMap,
  createLocalizedTranslations,
  hasLocalMessagesForLanguagePreference,
  LocalizedRichText,
  useLocalizedTranslations,
  useLocalization,
  LocalizedText,
} from 'localization'

const GigDetailTextKeys = {
  AddOn: 'dweb_common_add_on',
  SeattleCaption: 'dweb_delivery_details_seattle_caption',
  ViewStatement: 'dweb_delivery_details_view_statement',
  Delivery: 'dweb_delivery_details_delivery',
  Deliveries: 'dweb_common_deliveries',
  BasePay: 'dweb_delivery_details_base_pay',
  AdditionalSeattlePay: 'dweb_delivery_details_additional_seattle_pay',
  ReturnPay: 'dweb_delivery_details_return_pay',
  CancellationPay: 'dweb_delivery_details_cancellation_pay',
  GMT: 'dweb_delivery_details_gmt',
  GMTCaptionPt1: 'dweb_delivery_details_gmtcaption_pt1',
  GMTCaptionPt2: 'dweb_delivery_details_gmtcaption_pt2',
  Tip: 'dweb_delivery_details_tip',
  Additional: 'dweb_delivery_details_additional',
  TipFromRecipient: 'dweb_delivery_details_tip_from_recipient',
  TipFromSender: 'dweb_delivery_details_tip_from_sender',
  ExpenseReim: 'dweb_delivery_details_expense_reim',
  Pending: 'dweb_delivery_details_pending',
  Total: 'dweb_common_total',
  ExpenseLabel: 'dweb_delivery_details_expense_label',
  ExpenseCaption: 'dweb_delivery_details_expense_caption',
  Completed: 'dweb_common_completed',
  Canceled: 'dweb_common_canceled',
  DeliveryInfo: 'dweb_delivery_details_delivery_info',
  AttemptedDelivery: 'dweb_delivery_details_attempted_delivery',
  ReturnDelivery: 'dweb_delivery_details_return_delivery',
  Pickup: 'dweb_delivery_details_pickup',
  Cancellations: 'dweb_delivery_details_cancellations',
  CanceledByDriverAtPickup: 'dweb_delivery_details_canceled_by_driver_at_pickup',
  CanceledBySender: 'dweb_delivery_details_canceled_by_sender',
  CanceledBySupport: 'dweb_delivery_details_canceled_by_support',
  HelpButton: 'dweb_delivery_details_help_button',
  ContactSupport: 'dweb_common_contact_support',
  DuringBlockLink: 'dweb_delivery_details_during_block_link',
  AddExpense: 'dweb_delivery_details_add_expense',
  Parking: 'dweb_delivery_details_parking',
  Toll: 'dweb_delivery_details_toll',
  NoGas: 'dweb_delivery_details_no_gas',
  AddExpensePhotoCaption: 'dweb_delivery_details_add_expense_photo_caption',
  AddExpenseCaption: 'dweb_delivery_details_add_expense_caption',
  UploadImage: 'dweb_delivery_details_upload_image',
  TotalAmount: 'dweb_delivery_details_total_amount',
  onReloadMessage: 'dweb_delivery_details_on_reload_message',
  Submit: 'dweb_common_submit',
  ViewReceipt: 'dweb_delivery_details_view_receipt',
  Item: 'dweb_common_item',
  Items: 'dweb_common_items',
}

const GigDetailStaticText = createLocalizedTranslations(GigDetailTextKeys, getEnglishTextMap(GigDetailTextKeys))

// Parking and Toll Expense Submission Cutoff: 15 days (ms)
const EXPENSE_SUBMISSION_CUTOFF_IN_MS = 24 * 60 * 60 * 1000 * 15

function cancellationSourceApplicationToLabel(cancellationSourceApp, staticTextMap) {
  switch (cancellationSourceApp) {
    case 'ios':
    case 'android':
      return staticTextMap.CanceledByDriverAtPickup
    case 'sender_api':
    case 'home_depot_proxy':
    case 'web':
      return staticTextMap.CanceledBySender
    case 'admin':
      return staticTextMap.CanceledBySupport
    default:
      return ''
  }
}

function Price({ children }) {
  const theme = useTheme()
  return (
    <Body1
      emphasis="medium"
      style={{
        whiteSpace: 'nowrap',
        paddingTop: '0.3rem',
        color: theme.colors.black,
      }}
    >
      {children}
    </Body1>
  )
}

function TotalPrice({ children }) {
  return <Container.Base style={{ display: 'flex', alignItems: 'flex-start' }}>{children}</Container.Base>
}

const renderTotal = (num) => {
  const match = formatCurrency(num).match(/^\$(\d+)\.(\d{2})$/)
  if (match) {
    const [dollars, cents] = match.slice(1)
    return (
      <TotalPrice>
        <Price>$</Price>
        <Text.H4 style={{ margin: '0 0.2rem', fontWeight: 'normal' }}>{dollars}</Text.H4>
        <Price>{cents}</Price>
      </TotalPrice>
    )
  }
}

function DeliveryInfoLine({ children, isFirst, isLast }) {
  const theme = useTheme()
  const childArray = React.Children.toArray(children)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginRight: '2.4rem' }}>
      {childArray.map((child, idx) => {
        if (idx === 0) {
          return (
            <div key={idx} style={{ height: '2.6rem', flexShrink: 0 }}>
              <span
                style={{
                  height: '100%',
                  borderLeft: !isFirst ? `1px dashed ${theme.colors.greyLight}` : null,
                  content: "''",
                  marginLeft: '50%',
                }}
              />
              {child}
            </div>
          )
        }
        if (child?.type?.name === 'Icon' || idx === 1) {
          return (
            <div key={idx} style={{ minHeight: '2.4rem', minWidth: '2.4rem' }}>
              {child}
            </div>
          )
        }
        if (idx === childArray.length - 1) {
          return (
            <div
              key={idx}
              style={{
                flexShrink: 1,
                flexGrow: 1,
                borderLeft: !isLast ? `1px dashed ${theme.colors.greyLight}` : null,
                marginLeft: '50%',
                marginTop: '0.6rem',
                display: 'flex',
              }}
            >
              {child}
            </div>
          )
        }
        return child
      })}
    </div>
  )
}

function DeliveryInfoStop({ children }) {
  const isWebView = useSelector((state) => state.config.isWebview)
  return (
    <div
      style={{
        display: 'flex',
        flexGrow: 1,
        padding: isWebView ? '2.4rem 0 2.4rem 0' : '2.4rem 2.4rem 2.4rem 0',
      }}
    >
      {children}
    </div>
  )
}

function DeliveryInfoStopWrapper({ children }) {
  return <div style={{ width: '100%' }}>{children}</div>
}

function DeliveryInfoItem({ children, isLast }) {
  const theme = useTheme()
  const childArray = React.Children.toArray(children)

  return (
    <div style={{ display: 'flex' }}>
      {childArray.map((child) => {
        if (child.type === DeliveryInfoStopWrapper && !isLast) {
          return React.cloneElement(child, {
            style: {
              ...(child.props.style || {}),
              borderBottom: `1px solid ${theme.colors.greyLight}`,
            },
          })
        }
        return child
      })}
    </div>
  )
}

function SenderBadge({ children, ...props }) {
  const isWebView = useSelector((state) => state.config.isWebview)
  const style = {
    display: 'flex',
    ...(isWebView ? { paddingLeft: 0 } : {}),
  }

  return (
    <PanelTitle style={style} {...props}>
      {children}
    </PanelTitle>
  )
}

function SenderIcon(props) {
  return (
    <img
      style={{
        height: '1.6rem',
        width: '1.6rem',
        marginRight: '0.8rem',
        objectFit: 'scale-down',
        borderRadius: '50%',
      }}
      {...props}
    />
  )
}

function HelpArea() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    />
  )
}

function ModalPhoto(props) {
  return (
    <img
      style={{
        margin: 'auto',
        maxHeight: '70vh',
      }}
      {...props}
    />
  )
}

function ReceiptPreviewPhoto(props) {
  return (
    <img
      style={{
        width: 'auto',
        margin: 'auto',
        maxHeight: '30vh',
      }}
      {...props}
    />
  )
}

const GalleryReceipt = (props) => {
  const { src } = props
  return (
    <Container.Base $backgroundColor="neutral-black" $maxHeight="70vh">
      <ModalPhoto src={src} />
    </Container.Base>
  )
}

const Receipts = (props) => {
  const { urls, first, staticTextMap } = props
  const [open, setOpen] = useState(false)

  const handleToggle = useCallback(() => setOpen((was) => !was), [])

  if (open) {
    const set = new Set([first, ...urls])
    return (
      <Modal.Raw onRequestClose={handleToggle}>
        {set.size > 1 ? (
          <Gallery onRequestClose={handleToggle}>
            {Array.from(set).map((url) => (
              <GalleryReceipt key={url} src={url} />
            ))}
          </Gallery>
        ) : (
          <GalleryReceipt src={urls[0]} />
        )}
      </Modal.Raw>
    )
  }

  return (
    <Button.Div.Tertiary $fontSize="1.3rem" $textTransform="none" onClick={handleToggle}>
      {staticTextMap.ViewReceipt}
    </Button.Div.Tertiary>
  )
}

const AddExpense = (props) => {
  const { gigId, gigTitle, onReload, staticTextMap = GigDetailStaticText } = props
  const [adding, setAdding] = useState(false)
  const file = useRef()
  const dispatch = useDispatch()

  const toggleAdding = useCallback(() => {
    dispatch({ type: 'CLEAR_FLASH', id: 'expense' })
    setAdding((was) => !was)
  }, [dispatch])

  const handleFile = useCallback((upload) => {
    if (!upload || !upload.file) {
      return
    }
    file.current = upload.file
  }, [])

  const handleSubmit = useCallback(
    async (params) => {
      const data = new FormData()
      data.append('image', file.current)
      data.append('amount', params.amount)
      await api.patch(`gigs/${gigId}/images/expense`, data)
      onReload(staticTextMap.onReloadMessage)
    },
    [gigId, file, onReload, staticTextMap.onReloadMessage]
  )

  if (adding) {
    return (
      <Modal.Basic title={staticTextMap.AddExpense} onRequestClose={toggleAdding}>
        {
          <Photo onChange={handleFile} maxWidth={3000}>
            {(src) =>
              src ? (
                <Form.Basic
                  onSubmit={handleSubmit}
                  validations={[
                    ['amount', Form.validations.lte(26), 'Amount cannot exceed $26'],
                    ['amount', Form.validations.required, 'Please enter an amount'],
                  ]}
                >
                  <Text.Body2 $textAlign="left">{gigTitle}</Text.Body2>
                  <Spacer $height="1.8rem" />
                  <Container.Base $backgroundColor="neutral-black">
                    <ReceiptPreviewPhoto src={src} alt="" />
                  </Container.Base>
                  <Spacer $height="1.8rem" />
                  <Text.Body2 $textAlign="left" $color="neutral-black">
                    {staticTextMap.AddExpensePhotoCaption}
                  </Text.Body2>
                  <Spacer $height="1rem" />
                  <Container.Grid $gridTemplateColumns="1fr 1fr">
                    <Form.Input type="decimal" name="amount" label={staticTextMap.TotalAmount} />
                    <Container.GridItemGrid $justifyContent="end" $alignContent="end">
                      <Button.Tertiary>{staticTextMap.Submit}</Button.Tertiary>
                    </Container.GridItemGrid>
                  </Container.Grid>
                </Form.Basic>
              ) : (
                <div>
                  <Container.Grid
                    $gridTemplateColumns="10rem 1px 10rem 1px 10rem"
                    $alignItems="center"
                    $justifyItems="center"
                    $justifyContent="center"
                    $color="neutral-black"
                  >
                    <Container.GridItemGrid $justifyItems="center">
                      <RoadieUIIcon.ParkingMeter $width="4rem" />
                      <Text.Caption>{staticTextMap.Parking}</Text.Caption>
                    </Container.GridItemGrid>
                    <Container.Base $width="1px" $height="3rem" $backgroundColor="neutral-lighter" />
                    <Container.GridItemGrid $justifyItems="center">
                      <RoadieUIIcon.Toll $width="4rem" />
                      <Text.Caption>{staticTextMap.Toll}</Text.Caption>
                    </Container.GridItemGrid>
                    <Container.Base $width="1px" $height="3rem" $backgroundColor="neutral-lighter" />
                    <Container.GridItemGrid $justifyItems="center">
                      <IconStack width="4rem">
                        <RoadieUIIcon.GasPump />
                        <RoadieUIIcon.No $color="error-base" />
                      </IconStack>
                      <Text.Caption>{staticTextMap.NoGas}</Text.Caption>
                    </Container.GridItemGrid>
                  </Container.Grid>
                  <Spacer $height="3.2rem" />
                  <Text.Body2 $color="neutral-black">{staticTextMap.AddExpenseCaption}</Text.Body2>
                  <Spacer $height="3.2rem" />
                  <Button.Div.Primary $width="100%">{staticTextMap.UploadImage}</Button.Div.Primary>
                </div>
              )
            }
          </Photo>
        }
      </Modal.Basic>
    )
  }

  return (
    <Button.Secondary $widthMedia={{ mobile: '100%' }} onClick={toggleAdding}>
      {staticTextMap.AddExpense}
    </Button.Secondary>
  )
}

const DownloadConsolidationStatement = (props) => {
  const { consolidation, staticTextMap = GigDetailStaticText } = props
  const isWebview = useSelector((state) => state.config.isWebview)
  const handleDownload = React.useCallback(() => {
    api.downloadFile({
      path: `consolidations/${consolidation.id}/download_statement`,
      name: `consolidationStatement.pdf`,
    })
  }, [consolidation.id])

  if (isWebview) {
    return (
      <Text.Caption baseElement="span">
        <AdaptiveExternalLink to={`download/consolidationSeattleStatement/${consolidation.id}`}>
          {staticTextMap.ViewStatement}
        </AdaptiveExternalLink>
      </Text.Caption>
    )
  }
  return (
    <Button.Tertiary onClick={handleDownload} $display="contents" $fontSize="1.3rem" $textTransform="none">
      {staticTextMap.ViewStatement}
    </Button.Tertiary>
  )
}

const Consolidation = (props) => {
  const onReload = props.onReload
  const c = props.consolidation
  const gigs = props.gigs
  const complianceData = props.complianceData
  const staticTextMap = props.staticTextMap || GigDetailStaticText
  const blockData = props.blockData
  const { formatDate, toLocalizedTime } = useLocalization()
  const consolidationTextMap = {
    completed: staticTextMap.Completed || 'Completed',
    canceled: staticTextMap.Canceled || 'Canceled',
    add_on: staticTextMap.AddOn || 'Add-On',
  }
  const isPOTO = c.offer?.source === 'poto'
  const gigIdToGig = new Map(gigs.map((gig) => [gig.id, gig]))

  const expenses = c.gigs
    .map((g) => {
      const image = (g.images || []).find((i) => i.imageType === 'expense')
      if (!image) {
        return null
      }
      const pendingAmount = (g.pendingExpenses && g.pendingExpenses.expenses) || 0
      const approvedAmount = (g.payoutBreakdown.expenseAdjustment || 0) + (g.payoutBreakdown.tollAdjustment || 0)
      if (!pendingAmount && !approvedAmount) {
        return null
      }
      const amount = approvedAmount || pendingAmount
      const pending = !approvedAmount
      return { amount, pending, url: image.url }
    })
    .filter((e) => !!e)

  const nonExpensedGigs = c.gigs.filter((g) => {
    const image = (g.images || []).find((i) => i.imageType === 'expense')
    return !image
  })

  const deliveryCompletedTime = c.deliveryCompletedAt ? c.deliveryCompletedAt : false

  // Comparison done using UNIX time in milliseconds to avoid timezone issues
  const isAfterExpenseSubmissionCutoff =
    deliveryCompletedTime && Date.now() - new Date(deliveryCompletedTime).getTime() > EXPENSE_SUBMISSION_CUTOFF_IN_MS

  const isCanceled = c.state === 'canceled'
  const isSeattleCompliantGig = c.isSeattle && !!c.statementUrl

  const canExpense = nonExpensedGigs.length && !isAfterExpenseSubmissionCutoff && !isCanceled

  const expensesEnabled = gigs.find((g) => g.sender && g.sender.expensesEnabled)

  const [canceledGigs, nonCancelledGigs] = partition(gigs, (g) => g.status === 'cancelled')

  const [returnGigs, regularGigs] = partition(nonCancelledGigs, (g) => g.title.match(/\(RETURN\)/))

  const nonCancelledConsolidationGigs = nonCancelledGigs.map((g) => c.gigs.find((cg) => cg.id === g.id))

  const isColoradoBasedConsolidation =
    nonCancelledGigs
      .map((g) => ({
        delivery_state: g.deliveryLocation.state.toUpperCase(),
        pickup_state: g.pickupLocation.state.toUpperCase(),
      }))
      .filter((t) => t.delivery_state == 'CO' || t.pickup_state == 'CO').length > 0

  const recipientTips = nonCancelledConsolidationGigs
    .map((g) => ({
      amount: g.payoutBreakdown.netTip,
      gigTitle: g.title,
    }))
    .filter((t) => t.amount > 0)

  const senderTips = flatMap(nonCancelledGigs, (g) => {
    return g.pricing.lineItems.filter((li) => li.category === 'incidentals')
  })

  const guaranteedTip = c.payoutBreakdown.guaranteedTip

  const seattlePay = isSeattleCompliantGig ? (c.payoutBreakdown.seattleAdjustmentPay ?? 0) : 0
  const returnPay = returnGigs.map((g) => g.pricing.amountPaidToRoadie).reduce((a, b) => a + b, 0)
  const basePay = regularGigs.map((g) => g.pricing.amountPaidToRoadie).reduce((a, b) => a + b, 0)
  const cancelFee = c.payoutBreakdown.cancellationFee || 0

  const sender = c.senders && c.senders[0]
  const deliveryTimestamp = c.deliveryCompletedAt || c.canceledAt

  const notPendingExpenses = expenses.filter((e) => !e.pending).reduce((a, b) => a + b.amount, 0)

  let total = returnPay + basePay + seattlePay + cancelFee + notPendingExpenses + guaranteedTip

  if (recipientTips.length) {
    total += recipientTips.reduce((sum, tip) => sum + tip.amount, 0)
  }

  if (senderTips.length) {
    total += senderTips.reduce((sum, tip) => sum + tip.amount, 0)
  }

  function event(gig, type) {
    return gig.events.find((e) => e.eventType === type)
  }

  function getPickupDate(gig) {
    const pickedUp = event(gig, 'pickup_confirmed')
    const scheduled = gig.pickupTime
    if (pickedUp) {
      return new Date(pickedUp.createdAt)
    } else if (scheduled) {
      return new Date(scheduled)
    }
    return null
  }

  function getDeliveryDate(gig) {
    const delivered = event(gig, 'delivery_confirmed')
    const deadline = gig.delivery_deadline
    if (delivered) {
      return new Date(delivered.createdAt)
    } else if (gig.status === 'delivery_attempted') {
      return new Date(gig.updatedAt)
    } else if (deadline) {
      return new Date(deadline)
    }
    return null
  }

  function getPickupTimeString(gig) {
    const pickupDate = getPickupDate(gig)
    if (pickupDate) {
      return toLocalizedTime(pickupDate, c.pickupTimezone).getTimeString()
    }
    return null
  }

  function getDeliveryTimeString(gig) {
    const deliveryDate = getDeliveryDate(gig)
    if (deliveryDate) {
      return toLocalizedTime(deliveryDate, c.deliveryTimezone).getTimeString()
    }
    return null
  }

  function filterCanceledStops(consolidationStops, gigMap) {
    const nonCanceledStops = consolidationStops.filter((stop) => {
      const gig = gigMap.get(stop.gigIds[0])

      // Filter out stops where gig not found because we can't verify cancellation status
      if (!gig) {
        return false
      }

      const isCanceled = canceledGigs.find((g) => g.id === gig.id)
      return !isCanceled
    })

    return nonCanceledStops
  }

  function getTimestampForStop(stop, gig) {
    const isPickup = stop.category === 'pickup'
    const date = isPickup ? getPickupDate(gig) : getDeliveryDate(gig)

    if (date) {
      return date.getTime()
    }
    // Fallback: use the latest possible date to push items without times to the end
    return Number.MAX_SAFE_INTEGER
  }

  function sortStopsChronologically(stops, gigMap) {
    return stops.sort((stopA, stopB) => {
      const gigA = gigMap.get(stopA.gigIds[0])
      const gigB = gigMap.get(stopB.gigIds[0])

      if (!gigA || !gigB) return 0

      const timestampA = getTimestampForStop(stopA, gigA)
      const timestampB = getTimestampForStop(stopB, gigB)

      return timestampA - timestampB
    })
  }

  let renderedPickUpLocations = []
  let route = isColoradoBasedConsolidation && complianceData?.totalDistanceMiles ? [] : c.route
  const nonCanceledStops = filterCanceledStops(c.stops, gigIdToGig)
  const consolidationStops = sortStopsChronologically(nonCanceledStops, gigIdToGig)

  return (
    <div>
      <Panel left>
        <div>
          {isCanceled ? null : (
            <MapDisplay
              consolidation={c}
              gigs={gigs}
              height="30rem"
              showRoute={!isPOTO}
              route={route}
              showMileage={(!isColoradoBasedConsolidation || !complianceData?.totalDistanceMiles) && !isPOTO}
              mileageText={`${c.estimatedDistance} miles`}
              isPOTO={isPOTO}
            />
          )}
          {sender && <Spacer $height="2.4rem" />}
          {sender ? (
            <SenderBadge>
              <SenderIcon src={sender.avatarUrl} /> {sender.name}
            </SenderBadge>
          ) : (
            <SenderBadge>{'\u00a0'}</SenderBadge>
          )}
          <Text.H4
            $color="neutral-black"
            $fontWeight="600"
            $fontSizeMedia={{ ultrawide: '3.2rem', desktop: '3.2rem', mobile: '2.6rem' }}
          >
            {isPOTO && consolidationTextMap.add_on + ': '}
            {c.title}
          </Text.H4>
          <Spacer $height="1.6rem" />
          <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
            <RoadieUIIcon.Calendar />
            <Spacer $width=".8rem" />
            <Text.Body2>
              {capitalize(consolidationTextMap[c.state])}{' '}
              {toLocalizedTime(deliveryTimestamp, c.deliveryTimezone).toString()}
              {blockData?.scheduledStart && (
                <>
                  {' '}
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_during_block_link"
                    params={{
                      blockLink: (
                        <Link to={`/block-details/${blockData.blockId}`}>
                          <LocalizedRichText
                            translationKey="dweb_delivery_details_consolidation_details_value1_block"
                            params={{
                              value1: formatDate(blockData.scheduledStart, {
                                month: '2-digit',
                                day: '2-digit',
                              }),
                            }}
                          />
                        </Link>
                      ),
                    }}
                  />
                </>
              )}
            </Text.Body2>
          </Container.Grid>
          <Spacer $height="1.6rem" />
          {/* No translations applied for colorado consolidation fields */}
          {isColoradoBasedConsolidation && Boolean(complianceData?.totalDistanceMiles) && (
            <>
              <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
                <RoadieUIIcon.NorthArrow />
                <Spacer $width=".8rem" />
                <Text.Body2>
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_value1_miles"
                    params={{ value1: roundUpToOneDecimal(complianceData.totalDistanceMiles) }}
                  />
                </Text.Body2>
              </Container.Grid>
              <Spacer $height="1.6rem" />
            </>
          )}
          {isColoradoBasedConsolidation && Boolean(complianceData?.totalTimeWorkedSeconds) && (
            <>
              <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
                <RoadieUIIcon.ClockReverse />
                <Spacer $width=".8rem" />
                <Text.Body2>
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_value1_min"
                    params={{ value1: Math.ceil(complianceData.totalTimeWorkedSeconds / 60) }}
                  />
                </Text.Body2>
              </Container.Grid>
              <Spacer $height="1.6rem" />
            </>
          )}
          <Container.Grid $gridTemplateColumns="1.8rem .8rem max-content">
            <RoadieUIIcon.Package />
            <Spacer $width=".8rem" />
            {isColoradoBasedConsolidation ? (
              <Text.Body2>
                {gigs.length} {gigs.length < 2 ? 'Order' : 'Orders'}{' '}
                {canceledGigs.length != 0 && `(${canceledGigs.length} Canceled)`}
              </Text.Body2>
            ) : (
              <Text.Body2>
                {gigs.length} {staticTextMap.plural(gigs.length, { one: 'Delivery', other: 'Deliveries' })}
              </Text.Body2>
            )}
          </Container.Grid>
          <Spacer $height="3.2rem" />
          {isSeattleCompliantGig ? (
            <>
              <Container.Grid
                $borderColor="secondary-lighter"
                $borderWidth="1px"
                $borderStyle="solid"
                $borderRadius=".4rem"
                $gridTemplateColumns="auto 1fr"
                $alignItems="center"
              >
                <Container.Grid
                  $alignItems="center"
                  $backgroundColor="secondary-lighter"
                  $height="100%"
                  $padding="0 0.8rem"
                >
                  <RoadieUIIcon.AlertCircle $color="secondary-base" $height="1.8rem" />
                </Container.Grid>
                <Box.Padding $padding="0.8rem 1.6rem">
                  <Text.Body2 baseElement="span" $color="neutral-black">
                    {staticTextMap.SeattleCaption}{' '}
                  </Text.Body2>
                  <DownloadConsolidationStatement consolidation={c} staticTextMap={staticTextMap} />
                </Box.Padding>
              </Container.Grid>
              <Spacer $height="1.6rem" />
            </>
          ) : null}
          <Container.Grid $borderColor="neutral-light" $borderWidth="1px" $borderStyle="solid" $borderRadius=".4rem">
            <Container.Grid $padding="2.6rem 2.4rem" $gridGap="1.6rem">
              {nonCancelledGigs.length ? (
                <Container.Grid $gridTemplateColumns="3fr 1fr">
                  <div>
                    <Text.Body1 $color="neutral-black">{staticTextMap.BasePay}</Text.Body1>
                  </div>
                  <Text.Body1 $color="neutral-black" $textAlign="right">
                    {formatCurrency(basePay)}
                  </Text.Body1>
                </Container.Grid>
              ) : null}
              {isSeattleCompliantGig ? (
                <div>
                  <Container.Grid $gridTemplateColumns="3fr 1fr">
                    <div>
                      <Text.Body1 $color="neutral-black">{staticTextMap.AdditionalSeattlePay}</Text.Body1>
                    </div>
                    <Text.Body1 $color="neutral-black" $textAlign="right">
                      {formatCurrency(seattlePay)}
                    </Text.Body1>
                    <DownloadConsolidationStatement consolidation={c} staticTextMap={staticTextMap} />
                  </Container.Grid>
                </div>
              ) : null}
              {returnGigs.length ? (
                <div>
                  <Container.Grid $gridTemplateColumns="3fr 1fr">
                    <div>
                      <Text.Body1 $color="neutral-black">{staticTextMap.ReturnPay}</Text.Body1>
                      {returnGigs.map((g) => (
                        <Text.Caption key={`title-${g.id}`}>{g.title}</Text.Caption>
                      ))}
                    </div>
                    <Text.Body1 $color="neutral-black" $textAlign="right">
                      {formatCurrency(returnPay)}
                    </Text.Body1>
                  </Container.Grid>
                </div>
              ) : null}
              {canceledGigs.length && cancelFee != 0 ? (
                <div>
                  <Container.Grid $gridTemplateColumns="3fr 1fr">
                    <div>
                      <Text.Body1 $color="neutral-black">{staticTextMap.CancellationPay}</Text.Body1>
                      {canceledGigs.map((g) => (
                        <Text.Caption key={`title-${g.id}`}>{g.title}</Text.Caption>
                      ))}
                    </div>
                    <Text.Body1 $color="neutral-black" $textAlign="right">
                      {formatCurrency(cancelFee)}
                    </Text.Body1>
                  </Container.Grid>
                </div>
              ) : null}
              {guaranteedTip ? (
                <div>
                  <Container.Grid $gridTemplateColumns="3fr 1fr">
                    <div>
                      <Text.Body1 $color="neutral-black">{staticTextMap.GMT}</Text.Body1>
                      <Text.Caption>
                        {staticTextMap.GMTCaptionPt1} {formatCurrency(guaranteedTip / gigs.length)}{' '}
                        {staticTextMap.GMTCaptionPt2}
                      </Text.Caption>
                    </div>
                    <Text.Body1 $color="neutral-black" $textAlign="right">
                      {formatCurrency(guaranteedTip)}
                    </Text.Body1>
                  </Container.Grid>
                </div>
              ) : null}
              {recipientTips.length
                ? recipientTips.map((tip, index) => (
                    <Container.Grid key={`recipent-tip-${index}`} $gridTemplateColumns="3fr 1fr">
                      <div>
                        <Text.Body1 $color="neutral-black">
                          {tip.amount && !guaranteedTip ? '' : staticTextMap.Additional}
                          {staticTextMap.TipFromRecipient}
                        </Text.Body1>
                        <Text.Caption emphasis="medium">{tip.gigTitle}</Text.Caption>
                      </div>
                      <Text.Body1 $color="neutral-black" $textAlign="right">
                        {formatCurrency(tip.amount)}
                      </Text.Body1>
                    </Container.Grid>
                  ))
                : null}
              {senderTips.length
                ? senderTips.map((tip, index) => (
                    <Container.Grid key={`sender-tip-${index}`} $gridTemplateColumns="3fr 1fr">
                      <div>
                        <Text.Body1 $color="neutral-black">{staticTextMap.TipFromSender}</Text.Body1>
                        <Text.Caption>{tip.description}</Text.Caption>
                      </div>
                      <Text.Body1 $color="neutral-black" $textAlign="right">
                        {formatCurrency(tip.amount)}
                      </Text.Body1>
                    </Container.Grid>
                  ))
                : null}
              {expenses.map((expense) => (
                <Container.Grid key={expense.url} $gridTemplateColumns="3fr 1fr">
                  <div>
                    <Text.Body1 $color="neutral-black">{staticTextMap.ExpenseReim}</Text.Body1>
                    <Receipts first={expense.url} urls={expenses.map((e) => e.url)} staticTextMap={staticTextMap} />
                  </div>
                  <div>
                    <Text.Body1 $textAlign="right" $color={expense.pending ? 'neutral-base' : 'neutral-black'}>
                      {formatCurrency(expense.amount)}
                    </Text.Body1>
                    {expense.pending ? (
                      <Text.Overline $textAlign="right" $color="neutral-base">
                        {staticTextMap.Pending}
                      </Text.Overline>
                    ) : null}
                  </div>
                </Container.Grid>
              ))}
            </Container.Grid>
            <Container.Grid
              $gridTemplateColumns="1fr 1fr"
              $alignItems="center"
              $backgroundColor={parsers.surfaceColor.modify(
                'neutral-light',
                parsers.baselineColor.modifiers.opacity(0.3)
              )}
              $padding="1rem 2.4rem"
            >
              <Container.Grid $justifyItems="flex-start">
                <Text.Overline $color="neutral-black">{staticTextMap.Total}</Text.Overline>
              </Container.Grid>
              <Container.Grid $justifyItems="flex-end">
                <Text.H5 $color="neutral-black" $textAlign="right">
                  {renderTotal(total)}
                </Text.H5>
              </Container.Grid>
            </Container.Grid>
          </Container.Grid>
          {canExpense && expensesEnabled ? (
            <div>
              <Spacer $height="2.4rem" />
              <Text.Subtitle2 $color="neutral-black">{staticTextMap.ExpenseLabel}</Text.Subtitle2>
              <Spacer $height="0.4rem" />
              <Text.Caption>{staticTextMap.ExpenseCaption}</Text.Caption>
            </div>
          ) : null}
          <Spacer $height="1.6rem" />
        </div>
      </Panel>
      <PanelSpacer />
      <PanelTitle>{staticTextMap.DeliveryInfo}</PanelTitle>
      <Panel left>
        {canceledGigs.length != 0 && (
          <DeliveryInfoItem>
            <DeliveryInfoLine isFirst isLast>
              <div />
              <RoadieUIIcon.No $color="neutral-dark" $height="3rem" />
              <div />
            </DeliveryInfoLine>
            <DeliveryInfoStopWrapper>
              <DeliveryInfoStop>
                <div>
                  <Text.H6 $color="neutral-black">{staticTextMap.Cancellations}</Text.H6>
                  {canceledGigs.map((canceledGig) => {
                    const cancellationSourceLabel = cancellationSourceApplicationToLabel(
                      canceledGig.cancellationSourceApplication,
                      staticTextMap
                    )
                    const itemCount = canceledGig.items?.length || 0
                    const itemLabel = staticTextMap.plural(itemCount, { one: 'Item', other: 'Items' })
                    return (
                      <Text.Caption key={canceledGig.id} $color="neutral-base">
                        {canceledGig.title} ({itemCount} {itemLabel}){' '}
                        {cancellationSourceLabel && `- ${cancellationSourceLabel}`}
                      </Text.Caption>
                    )
                  })}
                  <Spacer $height="0.8rem" />
                </div>
              </DeliveryInfoStop>
            </DeliveryInfoStopWrapper>
          </DeliveryInfoItem>
        )}
        {isCanceled ? null : (
          <>
            {consolidationStops.map((stop, idx) => {
              const isPickup = stop.category === 'pickup'
              const isDelivery = stop.category === 'delivery'
              const gig = gigIdToGig.get(stop.gigIds[0])
              const isExpensed = (c.gigs.find((g) => g.id === gig.id).images || []).find(
                (i) => i.imageType === 'expense'
              )
              const expenseEnabled = gig.sender && gig.sender.expensesEnabled
              const isReturn = !!gig.title.match(/\(RETURN\)/)
              const isAttempted = stop.lastEvent === 'handoff_attempted'
              const title = isAttempted
                ? staticTextMap.AttemptedDelivery
                : isReturn
                  ? staticTextMap.ReturnDelivery
                  : isPickup
                    ? staticTextMap.Pickup
                    : isDelivery
                      ? staticTextMap.Delivery
                      : capitalize(stop.category)
              let datetime = isPickup ? getPickupTimeString(gig) : isDelivery ? getDeliveryTimeString(gig) : null

              if (isPickup) {
                // prevent pickups at the same stop from render
                let isPickUpAlreadyRendered = false

                for (let i = 0; i < renderedPickUpLocations.length; i++) {
                  if (
                    renderedPickUpLocations[i] &&
                    renderedPickUpLocations[i].latitude &&
                    renderedPickUpLocations[i].longitude &&
                    renderedPickUpLocations[i].latitude === stop.location.latitude &&
                    renderedPickUpLocations[i].longitude === stop.location.longitude
                  ) {
                    isPickUpAlreadyRendered = true
                    break
                  }
                }

                if (isPickUpAlreadyRendered) {
                  return null
                } else {
                  renderedPickUpLocations.push(stop.location)
                }
              }

              return (
                <DeliveryInfoItem key={stop.index} isLast={idx === consolidationStops.length - 1}>
                  <DeliveryInfoLine isFirst={idx === 0} isLast={idx === consolidationStops.length - 1}>
                    <div />
                    <Icon symbol={isAttempted ? 'stop-return' : isPickup ? 'stop-pickup' : 'stop-delivery'} />
                    <div />
                  </DeliveryInfoLine>
                  <DeliveryInfoStopWrapper>
                    <DeliveryInfoStop>
                      <div>
                        <Text.H6 $color="neutral-black">{title}</Text.H6>
                        {datetime && <Text.Caption $color="neutral-base">{datetime}</Text.Caption>}
                        {isDelivery ? (
                          <Text.Caption $color="neutral-base">
                            {gig.title}
                            {gig.items ? ` (${gig.items.length} ${gig.items.length === 1 ? 'item' : 'items'})` : ''}
                          </Text.Caption>
                        ) : (
                          <Text.Caption>{'\u00a0'}</Text.Caption>
                        )}
                        <Spacer $height="0.8rem" />
                        <Text.Body2 font$Weight="bold">{stop.location.city}</Text.Body2>
                        <Text.Body2>
                          {stop.location.state}, {stop.location.zip}
                        </Text.Body2>
                        <Spacer $height="0.8rem" />
                        {isDelivery && stop.supportIds && stop.supportIds.filter((x) => x).length ? (
                          <Text.Caption $emphasis="low">
                            <LocalizedRichText
                              translationKey="dweb_delivery_details_consolidation_details_support_id_value1"
                              params={{
                                value1: stop.supportIds.map((sid) => sid.substr(0, 6).toUpperCase()).join(', '),
                              }}
                            />
                          </Text.Caption>
                        ) : (
                          <Text.Caption>{'\u00a0'}</Text.Caption>
                        )}
                      </div>
                    </DeliveryInfoStop>
                    {isPickup || isExpensed || !canExpense || !expenseEnabled ? null : (
                      <AddExpense
                        gigId={gig.id}
                        gigTitle={gig.title}
                        onReload={onReload}
                        staticTextMap={staticTextMap}
                      />
                    )}
                    <Spacer $height="2.4rem" />
                  </DeliveryInfoStopWrapper>
                </DeliveryInfoItem>
              )
            })}
          </>
        )}
      </Panel>
      <Spacer $height="6.4rem" />
      <HelpArea>
        <ButtonText>{staticTextMap.HelpButton}</ButtonText>
        <Spacer $height="1.2rem" />
        <ExternalLink href="https://support.roadie.com/support/s/contactsupport">
          {staticTextMap.ContactSupport}
        </ExternalLink>
      </HelpArea>
    </div>
  )
}

const ConsolidationDetailsPage = (props) => {
  const { t } = useLocalization()
  const localizedTranslations = useLocalizedTranslations(GigDetailTextKeys)
  const localizedStaticText = localizedTranslations.map
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(true)

  const [consolidation, setConsolidation] = useState(null)
  const [gigs, setGigs] = useState(null)
  const [reload, setReload] = useState(0)
  const [complianceData, setComplianceData] = useState(null)

  const accountSettings = useSelector((state) => state.accountSettings)
  const languagePreference = getLatestAccountSetting(accountSettings, 'preferred_language') || 'english'
  const [translationMap, setTranslationMap] = useState(localizedStaticText)
  const translations = useMemo(() => createLocalizedTranslations(GigDetailTextKeys, translationMap), [translationMap])
  const [loadingTranslation, setLoadingTranslation] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState('english')
  const googleTranslateTargetLanguage = getGoogleTranslateTargetLanguage(languagePreference)
  const shouldOfferGoogleTranslation =
    Boolean(googleTranslateTargetLanguage) && !hasLocalMessagesForLanguagePreference(languagePreference)

  const cid = props.match.params.consolidation_id

  const handleReload = useCallback(
    (msg) => {
      setReload((r) => r + 1)
      if (msg) {
        dispatch({
          type: 'PUSH_FLASH',
          flash: {
            id: 'expense',
            type: 'success',
            message: msg,
          },
        })
      }
    },
    [dispatch]
  )

  useEffect(() => {
    const handleTranslate = async () => {
      setLoadingTranslation(true)
      if (currentLanguage != 'english') {
        setTranslationMap(localizedStaticText)
        setCurrentLanguage('english')
        setLoadingTranslation(false)
        dispatch({
          type: 'CLEAR_ALL_FLASH',
        })
        return
      } else {
        if (!googleTranslateTargetLanguage) {
          setLoadingTranslation(false)
          return
        }

        try {
          const translatedMap = await translateText(localizedStaticText, googleTranslateTargetLanguage)
          setTranslationMap(translatedMap)
          setCurrentLanguage(languagePreference)
          setLoadingTranslation(false)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
        } catch (error) {
          console.error('Translation failed:', error)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              message: (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_translation_failed_try_again" />
                </Text.Body1>
              ),
              type: 'error',
            },
          })
        }
      }
    }

    if (shouldOfferGoogleTranslation) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          type: 'info',
          message: (
            <Container.Grid
              onClick={handleTranslate}
              $display="flex"
              $textAlign="center"
              $alignItems="center"
              $justifyContent="center"
              $gridGap="1rem"
            >
              <RoadieUIIcon.Translate $color="neutral-white" $width="2.4rem" />
              {loadingTranslation ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_delivery_details_consolidation_details_translating" />
                </Text.Body1>
              ) : currentLanguage === 'english' ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_tap_translate_strong_text1"
                    params={{ strongText1: <strong>{capitalize(languagePreference)}</strong> }}
                  />
                </Text.Body1>
              ) : (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_viewing_strong_text1_tap_revert_english"
                    params={{ strongText1: <strong>{capitalize(currentLanguage)}</strong> }}
                  />
                </Text.Body1>
              )}
            </Container.Grid>
          ),
        },
      })
    }
  }, [
    accountSettings,
    languagePreference,
    dispatch,
    currentLanguage,
    loadingTranslation,
    localizedStaticText,
    googleTranslateTargetLanguage,
    shouldOfferGoogleTranslation,
  ])

  useEffect(() => {
    if (currentLanguage === 'english') setTranslationMap(localizedStaticText)
  }, [currentLanguage, localizedStaticText])

  useEffect(() => {
    dispatch(fetchConsolidationDetails(cid)).then((data) => {
      if (data) {
        setConsolidation(data.consolidation)
        setGigs(data.gigs)
        setLoading(false)
        setComplianceData(data.complianceData)
      }
    })
  }, [cid, dispatch, reload])

  const firstGigId = consolidation?.gigs && consolidation.gigs?.length > 0 ? consolidation.gigs[0].id : undefined
  const { data: blockData, isLoading: isBlockDataLoading } = useBlockData(firstGigId)

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_delivery_details_consolidation_details_gig_details')}</title>
      </Helmet>

      {loading || isBlockDataLoading ? (
        <StaticLoader />
      ) : (
        <Consolidation
          key={reload}
          onReload={handleReload}
          consolidation={consolidation}
          gigs={gigs}
          complianceData={complianceData}
          staticTextMap={translations || GigDetailStaticText}
          blockData={blockData}
        />
      )}
    </Page>
  )
}

export default ConsolidationDetailsPage
